import { mvpLimits } from "./limits.js";
import {
  chooseNextScene,
  createRuntime,
  getAvailableChoices,
  isCurrentSceneEnding
} from "./runtime.js";
import {
  emotions,
  sectionCueKinds,
  type Asset,
  type AssetType,
  type Novel,
  type Scene,
  type Visibility
} from "./types.js";

const allowedSceneKeys = new Set([
  "id",
  "title",
  "background_asset_id",
  "music_asset_id",
  "characters_present",
  "character_emotions",
  "emotion_cues",
  "text",
  "choices",
  "is_ending",
  "state_changes",
  "is_continuation",
  "sections"
]);
const allowedSectionKeys = new Set([
  "id",
  "text",
  "break_before",
  "characters",
  "emotions",
  "background_asset_id",
  "cues"
]);
const allowedCueKeys: Record<string, Set<string>> = {
  sound: new Set(["id", "kind", "asset_id", "volume", "loop"]),
  frame: new Set(["id", "kind", "asset_id"])
};
/** Asset types each cue kind may reference. */
const cueAssetTypes: Record<string, ReadonlySet<AssetType>> = {
  sound: new Set<AssetType>(["sound", "music"]),
  frame: new Set<AssetType>(["illustration", "video", "background", "cover", "ending_image"])
};
const emotionValues = new Set<string>(emotions);
const allowedChoiceKeys = new Set([
  "id",
  "text",
  "next_scene_id",
  "effects",
  "required_state"
]);

const allowedVisibilityValues = new Set<Visibility>([
  "draft",
  "private",
  "unlisted",
  "public",
  "under_review",
  "hidden"
]);

export type ValidationCode =
  | "invalid_owner_id"
  | "invalid_visibility"
  | "missing_title"
  | "missing_description"
  | "missing_cover"
  | "missing_start_scene"
  | "missing_ending"
  | "duplicate_scene_id"
  | "broken_scene_link"
  | "broken_ending_link"
  | "unreachable_scene"
  | "empty_scene_text"
  | "too_many_scenes"
  | "too_many_characters"
  | "too_many_endings"
  | "too_many_choices"
  | "scene_text_too_long"
  | "missing_required_asset"
  | "dead_end_scene"
  | "no_path_to_ending"
  | "unknown_property"
  | "soft_lock_risk"
  | "invalid_support_link"
  | "invalid_asset_url"
  | "invalid_section"
  | "public_projection";

export interface ValidationError {
  code: ValidationCode;
  message: string;
  sceneId?: string;
  choiceId?: string;
  assetId?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * The scene ceiling that applies to a specific novel. Continuation raises it
 * above the base `mvpLimits.maxScenes` by `scenesPerContinuation` per pass, so
 * a novel legitimately grown with "догенерация" is not flagged as oversized.
 */
export function effectiveMaxScenes(novel: Pick<Novel, "continuation_count">): number {
  const passes = Math.max(0, Math.floor(novel.continuation_count ?? 0));
  return mvpLimits.maxScenes + passes * mvpLimits.scenesPerContinuation;
}

export interface ValidateNovelGraphOptions {
  /**
   * Lower bound for the scene ceiling. Grandfathers already-stored novels when
   * the base limit is tightened: a novel that was persistable under a prior,
   * larger limit must stay updatable/publishable. The repository passes the
   * *existing* novel's scene count here on update/publish; create passes nothing
   * so fresh content still respects the base (continuation-aware) limit. It only
   * raises the ceiling, never lowers it.
   */
  maxScenesFloor?: number;
}

export function validateNovelGraph(
  novel: Novel,
  options: ValidateNovelGraphOptions = {}
): ValidationResult {
  const errors: ValidationError[] = [];
  const sceneById = new Map<string, Scene>();
  const seenScenes = new Set<string>();

  if (!isSafeOwnerId(novel.owner_id)) {
    errors.push({
      code: "invalid_owner_id",
      message: "Novel owner_id must contain only letters, numbers, underscores, and hyphens."
    });
  }

  // The reader copy (publicNovelProjection) has no generation input/trace, image
  // prompts or character profiles: saving it would erase them from the novel.
  if (novel.public_projection !== undefined) {
    errors.push({
      code: "public_projection",
      message: "This is the reader copy of the novel; reload it as its owner before saving."
    });
  }

  if (!isAllowedVisibility(novel.visibility)) {
    errors.push({
      code: "invalid_visibility",
      message: "Novel visibility must be one of: draft, private, unlisted, public, under_review, hidden."
    });
  }

  if (novel.support_link !== undefined && !isValidSupportLink(novel.support_link)) {
    errors.push({
      code: "invalid_support_link",
      message:
        "Support link must be an http(s) URL (under 500 chars) to a supported donation platform (DonationAlerts, Boosty, Ko-fi, Patreon, Buy Me a Coffee, PayPal.me, YooMoney)."
    });
  }

  // Asset URLs are author-editable and rendered straight into reader img/CSS
  // sinks, so a hostile scheme (javascript:, file:, …) or credentialed URL must
  // never be persisted. Runs on every save (validateNovelGraph precedes saveNovel).
  for (const asset of novel.assets) {
    if (!isSafeAssetUrl(asset.url)) {
      errors.push({
        code: "invalid_asset_url",
        message: `Asset "${asset.id}" has an unsafe url; allowed: http(s) URLs, data URIs, and same-origin paths.`,
        assetId: asset.id
      });
    }
  }

  const assetById = new Map(novel.assets.map((asset) => [asset.id, asset]));

  const maxScenes = Math.max(effectiveMaxScenes(novel), options.maxScenesFloor ?? 0);
  if (novel.scenes.length > maxScenes) {
    errors.push({
      code: "too_many_scenes",
      message: `Novel has more than ${maxScenes} scenes.`
    });
  }

  if (novel.characters.length > mvpLimits.maxCharacters) {
    errors.push({
      code: "too_many_characters",
      message: `Novel has more than ${mvpLimits.maxCharacters} characters.`
    });
  }

  if (novel.endings.length > mvpLimits.maxEndings) {
    errors.push({
      code: "too_many_endings",
      message: `Novel has more than ${mvpLimits.maxEndings} endings.`
    });
  }

  for (const scene of novel.scenes) {
    for (const key of Object.keys(scene)) {
      if (!allowedSceneKeys.has(key)) {
        errors.push({
          code: "unknown_property",
          message: `Scene "${scene.id}" contains unknown property "${key}".`,
          sceneId: scene.id
        });
      }
    }

    if (seenScenes.has(scene.id)) {
      errors.push({
        code: "duplicate_scene_id",
        message: `Scene ID "${scene.id}" is duplicated.`,
        sceneId: scene.id
      });
      continue;
    }

    seenScenes.add(scene.id);
    sceneById.set(scene.id, scene);

    if (scene.text.trim().length === 0) {
      errors.push({
        code: "empty_scene_text",
        message: `Scene "${scene.id}" has empty text.`,
        sceneId: scene.id
      });
    }

    if (scene.text.length > mvpLimits.maxSceneTextLength) {
      errors.push({
        code: "scene_text_too_long",
        message: `Scene "${scene.id}" exceeds text length limit.`,
        sceneId: scene.id
      });
    }

    if (scene.choices.length > mvpLimits.maxChoicesPerScene) {
      errors.push({
        code: "too_many_choices",
        message: `Scene "${scene.id}" has too many choices.`,
        sceneId: scene.id
      });
    }

    if (scene.sections !== undefined) {
      validateSceneSections(scene, assetById, errors);
    }

    for (const choice of scene.choices) {
      for (const key of Object.keys(choice)) {
        if (!allowedChoiceKeys.has(key)) {
          errors.push({
            code: "unknown_property",
            message: `Choice "${choice.id}" contains unknown property "${key}".`,
            sceneId: scene.id,
            choiceId: choice.id
          });
        }
      }
    }
  }

  if (!sceneById.has(novel.start_scene_id)) {
    errors.push({
      code: "missing_start_scene",
      message: `Start scene "${novel.start_scene_id}" does not exist.`,
      sceneId: novel.start_scene_id
    });
  }

  if (novel.endings.length === 0) {
    errors.push({
      code: "missing_ending",
      message: "Novel must have at least one ending."
    });
  }

  const endingSceneIds = new Set(novel.endings.map((ending) => ending.scene_id));

  for (const ending of novel.endings) {
    if (!sceneById.has(ending.scene_id)) {
      errors.push({
        code: "broken_ending_link",
        message: `Ending "${ending.id}" references missing scene "${ending.scene_id}".`,
        sceneId: ending.scene_id
      });
    }
  }

  for (const scene of novel.scenes) {
    for (const choice of scene.choices) {
      if (!sceneById.has(choice.next_scene_id)) {
        errors.push({
          code: "broken_scene_link",
          message: `Choice "${choice.id}" points to missing scene "${choice.next_scene_id}".`,
          sceneId: scene.id,
          choiceId: choice.id
        });
      }
    }
  }

  if (sceneById.has(novel.start_scene_id)) {
    const reachableSceneIds = getReachableSceneIds(novel);
    const canReachEnding = getEndingReachability(novel, endingSceneIds);

    for (const scene of novel.scenes) {
      if (!reachableSceneIds.has(scene.id)) {
        errors.push({
          code: "unreachable_scene",
          message: `Scene "${scene.id}" is unreachable from the start scene.`,
          sceneId: scene.id
        });
      }
    }

    for (const sceneId of reachableSceneIds) {
      const scene = sceneById.get(sceneId);
      if (!scene) {
        continue;
      }

      const isEnding = scene.is_ending || endingSceneIds.has(scene.id);

      if (!isEnding && scene.choices.length === 0) {
        errors.push({
          code: "dead_end_scene",
          message: `Reachable non-ending scene "${scene.id}" has no choices.`,
          sceneId: scene.id
        });
      }

      if (!canReachEnding.get(scene.id)) {
        errors.push({
          code: "no_path_to_ending",
          message: `Reachable scene "${scene.id}" cannot reach an ending.`,
          sceneId: scene.id
        });
      }
    }

    validateGreedyPathForSoftLocks(novel, errors);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * A scene's section timeline must be well-formed: unique ids, string text (a
 * blank frame is tolerated — the editor's new, not-yet-typed frame; readers
 * skip it),
 * at most two on-stage characters (all from the scene's roster), known
 * emotions, and cues/locations that point at existing assets of a fitting
 * type. Whether the sections still spell out `scene.text` is NOT checked —
 * readers re-segment drifted prose (`resolveSceneSections`), so an AI rewrite
 * that only touched `text` never blocks a save.
 */
function validateSceneSections(
  scene: Scene,
  assetById: ReadonlyMap<string, Asset>,
  errors: ValidationError[]
): void {
  const fail = (message: string) =>
    errors.push({ code: "invalid_section", message: `Scene "${scene.id}": ${message}`, sceneId: scene.id });
  const sections: unknown = scene.sections;
  if (!Array.isArray(sections)) {
    fail("sections must be an array.");
    return;
  }
  if (sections.length > mvpLimits.maxSectionsPerScene) {
    fail(`more than ${mvpLimits.maxSectionsPerScene} sections.`);
  }
  const roster = new Set(scene.characters_present);
  const ids = new Set<string>();
  for (const raw of sections) {
    if (!raw || typeof raw !== "object") {
      fail("a section is not an object.");
      continue;
    }
    const section = raw as Record<string, unknown>;
    const id = typeof section.id === "string" ? section.id : "";
    for (const key of Object.keys(section)) {
      if (!allowedSectionKeys.has(key)) fail(`section "${id}" has unknown property "${key}".`);
    }
    if (!id || id.length > 64) fail("a section has a missing or over-long id.");
    else if (ids.has(id)) fail(`section id "${id}" is duplicated.`);
    ids.add(id);
    if (typeof section.text !== "string") {
      fail(`section "${id}" text must be a string.`);
    } else if (section.text.length > mvpLimits.maxSceneTextLength) {
      fail(`section "${id}" text is too long.`);
    }
    if (
      section.break_before !== undefined &&
      section.break_before !== "line" &&
      section.break_before !== "paragraph"
    ) {
      fail(`section "${id}" has an invalid break_before.`);
    }
    const onStage = Array.isArray(section.characters) ? (section.characters as unknown[]) : null;
    if (!onStage || onStage.some((c) => typeof c !== "string")) {
      fail(`section "${id}" characters must be a list of character ids.`);
    } else {
      if (onStage.length > mvpLimits.maxSectionCharacters) {
        fail(`section "${id}" puts more than ${mvpLimits.maxSectionCharacters} characters on stage.`);
      }
      if (new Set(onStage).size !== onStage.length) fail(`section "${id}" repeats a character.`);
      for (const cid of onStage as string[]) {
        if (!roster.has(cid)) fail(`section "${id}" stages "${cid}", who is not in the scene.`);
      }
    }
    if (section.emotions !== undefined) {
      const map = section.emotions;
      if (!map || typeof map !== "object" || Array.isArray(map)) {
        fail(`section "${id}" emotions must be an object.`);
      } else {
        for (const [cid, emotion] of Object.entries(map)) {
          if (!onStage?.includes(cid)) fail(`section "${id}" sets an emotion for "${cid}", who is not on stage.`);
          if (typeof emotion !== "string" || !emotionValues.has(emotion)) {
            fail(`section "${id}" has an unknown emotion for "${cid}".`);
          }
        }
      }
    }
    if (section.background_asset_id !== undefined) {
      const bg = section.background_asset_id;
      if (typeof bg !== "string" || assetById.get(bg)?.type !== "background") {
        fail(`section "${id}" points at a location that does not exist.`);
      }
    }
    if (section.cues !== undefined) {
      validateSectionCues(id, section.cues, assetById, fail);
    }
  }
}

function validateSectionCues(
  sectionId: string,
  cues: unknown,
  assetById: ReadonlyMap<string, Asset>,
  fail: (message: string) => void
): void {
  if (!Array.isArray(cues)) {
    fail(`section "${sectionId}" cues must be an array.`);
    return;
  }
  if (cues.length > mvpLimits.maxSectionCues) {
    fail(`section "${sectionId}" has more than ${mvpLimits.maxSectionCues} cues.`);
  }
  const ids = new Set<string>();
  let frames = 0;
  for (const raw of cues) {
    if (!raw || typeof raw !== "object") {
      fail(`section "${sectionId}" has a cue that is not an object.`);
      continue;
    }
    const cue = raw as Record<string, unknown>;
    const kind = typeof cue.kind === "string" ? cue.kind : "";
    if (!(sectionCueKinds as readonly string[]).includes(kind)) {
      fail(`section "${sectionId}" has a cue of unknown kind "${kind}".`);
      continue;
    }
    for (const key of Object.keys(cue)) {
      if (!allowedCueKeys[kind].has(key)) fail(`section "${sectionId}" cue has unknown property "${key}".`);
    }
    const cueId = typeof cue.id === "string" ? cue.id : "";
    if (!cueId || ids.has(cueId)) fail(`section "${sectionId}" has a cue with a missing or duplicate id.`);
    ids.add(cueId);
    const asset = typeof cue.asset_id === "string" ? assetById.get(cue.asset_id) : undefined;
    if (!asset || !cueAssetTypes[kind].has(asset.type)) {
      fail(`section "${sectionId}" ${kind} cue points at a missing or unsuitable asset.`);
    }
    if (kind === "frame") frames += 1;
    if (kind === "sound") {
      if (cue.volume !== undefined && (typeof cue.volume !== "number" || !(cue.volume >= 0 && cue.volume <= 1))) {
        fail(`section "${sectionId}" sound cue volume must be between 0 and 1.`);
      }
      if (cue.loop !== undefined && typeof cue.loop !== "boolean") {
        fail(`section "${sectionId}" sound cue loop must be true or false.`);
      }
    }
  }
  if (frames > 1) fail(`section "${sectionId}" has more than one frame replacement.`);
}

function validateGreedyPathForSoftLocks(
  novel: Novel,
  errors: ValidationError[]
): void {
  let runtime = createRuntime(novel);
  const visited = new Set<string>();

  while (!visited.has(runtime.currentSceneId) && !isCurrentSceneEnding(runtime)) {
    visited.add(runtime.currentSceneId);
    const choices = getAvailableChoices(runtime);

    if (choices.length === 0) {
      const scene = runtime.novel.scenes.find(
        (candidate) => candidate.id === runtime.currentSceneId
      );
      if (scene && scene.choices.length > 0) {
        errors.push({
          code: "soft_lock_risk",
          message: `Reachable non-ending scene "${scene.id}" has no available choices for the accumulated world state.`,
          sceneId: scene.id
        });
      }
      return;
    }

    const nextChoice = choices[0];
    if (!nextChoice || !novel.scenes.some((scene) => scene.id === nextChoice.next_scene_id)) {
      return;
    }
    runtime = chooseNextScene(runtime, nextChoice.id);
  }
}

export function isSafeOwnerId(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]+$/.test(value);
}

export function isAllowedVisibility(value: unknown): value is Visibility {
  return typeof value === "string" && allowedVisibilityValues.has(value as Visibility);
}

/**
 * Donation platforms an author support link may point at. Restricting to a
 * whitelist is the anti-scam / moderation measure (EPIC H3): a support link
 * can only send readers to a known crowdfunding host, never an arbitrary URL.
 * Matched case-insensitively against the URL host, allowing subdomains.
 */
export const supportLinkAllowedHosts = [
  "donationalerts.com",
  "donationalerts.ru",
  "boosty.to",
  "ko-fi.com",
  "patreon.com",
  "buymeacoffee.com",
  "paypal.me",
  "yoomoney.ru"
] as const;

/** True when `host` equals or is a subdomain of an allowed donation host. */
function isAllowedSupportHost(host: string): boolean {
  // Drop a single trailing dot so a fully-qualified "boosty.to." still matches.
  const h = host.toLowerCase().replace(/\.$/, "");
  return supportLinkAllowedHosts.some((allowed) => h === allowed || h.endsWith(`.${allowed}`));
}

/**
 * A valid author support link: a plain http(s) URL under 500 chars, with no
 * embedded credentials, pointing at a whitelisted donation host (see
 * `supportLinkAllowedHosts`). An empty string is treated as "no link set" and is
 * valid (the editor stores "" when the author clears the field). Other protocols
 * (javascript:, data:, mailto:, …), off-whitelist hosts, and `user:pass@host`
 * phishing forms are rejected, so the link renders as a safe, moderated anchor.
 */
export function isValidSupportLink(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  if (trimmed.length > 500) return false;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  // Reject `https://good.host@evil.host/…` and other credential-bearing URLs.
  if (url.username !== "" || url.password !== "") return false;
  return isAllowedSupportHost(url.hostname);
}

/**
 * A support link an author may SET now: everything `isValidSupportLink`
 * requires, over https only (or empty = clear it). `isValidSupportLink` keeps
 * accepting http:// because links saved before https was required still render
 * and may be sent back unchanged; a new or edited link must be https.
 */
export function isValidNewSupportLink(value: unknown): value is string {
  if (!isValidSupportLink(value)) return false;
  const trimmed = value.trim();
  return trimmed.length === 0 || new URL(trimmed).protocol === "https:";
}

/**
 * A safe asset URL: an http(s) URL without embedded credentials, a data: URI
 * (placeholder providers / inline SVGs — inert in img/CSS sinks), or a schemeless
 * relative path (the placeholder SVGs and same-origin assets). Empty/absent is
 * "not generated yet" and allowed. Rejects credential-bearing URLs and
 * script-bearing schemes (javascript:, vbscript:, file:, …) so a hostile asset
 * URL can't be persisted and rendered into a reader's page.
 *
 * NOTE: this does not restrict the http(s) HOST, so a cross-origin tracking
 * beacon to the author's own server is still possible — narrowing to the
 * operator's asset host(s) needs deploy-time config the domain layer doesn't have.
 */
export function isSafeAssetUrl(value: unknown): boolean {
  if (typeof value !== "string") return true;
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  if (trimmed.length > 2000) return false;
  // No scheme → a relative path (placeholder SVGs, same-origin assets). Safe.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return true;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }
  if (url.protocol === "data:") return true;
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  if (url.username !== "" || url.password !== "") return false;
  return true;
}

export function validatePublicNovel(novel: Novel): ValidationResult {
  const graphResult = validateNovelGraph(novel);
  const errors = [...graphResult.errors];
  const readyAssets = new Map(
    novel.assets
      .filter((asset) => asset.status === "ready")
      .map((asset) => [asset.id, asset])
  );

  if (novel.title.trim().length === 0) {
    errors.push({ code: "missing_title", message: "Public novel needs a title." });
  }

  if (novel.description.trim().length === 0) {
    errors.push({
      code: "missing_description",
      message: "Public novel needs a description."
    });
  }

  if (!hasReadyAsset(readyAssets, novel.cover_asset_id, "cover")) {
    errors.push({
      code: "missing_cover",
      message: "Public novel needs a ready cover asset.",
      assetId: novel.cover_asset_id
    });
  }

  for (const scene of novel.scenes) {
    if (!hasReadyAsset(readyAssets, scene.background_asset_id, "background")) {
      errors.push({
        code: "missing_required_asset",
        message: `Scene "${scene.id}" is missing a ready background asset.`,
        sceneId: scene.id,
        assetId: scene.background_asset_id
      });
    }

    if (!hasReadyAsset(readyAssets, scene.music_asset_id, "music")) {
      errors.push({
        code: "missing_required_asset",
        message: `Scene "${scene.id}" is missing a ready music asset.`,
        sceneId: scene.id,
        assetId: scene.music_asset_id
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

export function getReachableSceneIds(novel: Novel): Set<string> {
  return getReachableSceneIdsFrom(novel, novel.start_scene_id);
}

/**
 * Every scene id reachable by following choices from `fromSceneId` (inclusive),
 * cycle-safe. `getReachableSceneIds` is the start-scene case. Used by the
 * "rewrite branch" flow to regenerate a scene and everything downstream of it.
 */
export function getReachableSceneIdsFrom(novel: Novel, fromSceneId: string): Set<string> {
  const sceneById = new Map(novel.scenes.map((scene) => [scene.id, scene]));
  const reachable = new Set<string>();
  const stack = [fromSceneId];

  while (stack.length > 0) {
    const sceneId = stack.pop();
    if (!sceneId || reachable.has(sceneId)) {
      continue;
    }

    const scene = sceneById.get(sceneId);
    if (!scene) {
      continue;
    }

    reachable.add(scene.id);
    for (const choice of scene.choices) {
      stack.push(choice.next_scene_id);
    }
  }

  return reachable;
}

/**
 * Every scene id that can reach `toSceneId` by following choices (inclusive),
 * cycle-safe. The graph stores only forward edges (`choice.next_scene_id`), so
 * this inverts them into a predecessor map first.
 */
export function getSceneIdsThatReach(novel: Novel, toSceneId: string): Set<string> {
  const predecessors = new Map<string, string[]>();
  for (const scene of novel.scenes) {
    for (const choice of scene.choices) {
      const list = predecessors.get(choice.next_scene_id);
      if (list) list.push(scene.id);
      else predecessors.set(choice.next_scene_id, [scene.id]);
    }
  }

  const sceneIds = new Set(novel.scenes.map((scene) => scene.id));
  const canReach = new Set<string>();
  const stack = [toSceneId];

  while (stack.length > 0) {
    const sceneId = stack.pop();
    if (!sceneId || canReach.has(sceneId) || !sceneIds.has(sceneId)) {
      continue;
    }
    canReach.add(sceneId);
    for (const predecessor of predecessors.get(sceneId) ?? []) {
      stack.push(predecessor);
    }
  }

  return canReach;
}

/**
 * The scenes lying on some path from `fromSceneId` to `toSceneId`, inclusive of
 * both ends — the set of scenes an author means by "the stretch from A to B".
 *
 * A scene qualifies when it is reachable from A **and** can still reach B, so
 * side branches that leave the corridor and never come back are excluded. When
 * B is not reachable from A at all the result is empty, which callers should
 * treat as "that range does not exist" rather than as "nothing to do".
 */
export function getScenesBetween(
  novel: Novel,
  fromSceneId: string,
  toSceneId: string
): Set<string> {
  const forward = getReachableSceneIdsFrom(novel, fromSceneId);
  if (!forward.has(toSceneId)) return new Set<string>();
  const backward = getSceneIdsThatReach(novel, toSceneId);
  const between = new Set<string>();
  for (const sceneId of forward) {
    if (backward.has(sceneId)) between.add(sceneId);
  }
  return between;
}

function getEndingReachability(
  novel: Novel,
  endingSceneIds: Set<string>
): Map<string, boolean> {
  const sceneById = new Map(novel.scenes.map((scene) => [scene.id, scene]));
  const memo = new Map<string, boolean>();

  function visit(sceneId: string, visiting: Set<string>): boolean {
    const scene = sceneById.get(sceneId);
    if (!scene) {
      return false;
    }

    if (scene.is_ending || endingSceneIds.has(scene.id)) {
      memo.set(scene.id, true);
      return true;
    }

    const memoized = memo.get(scene.id);
    if (memoized !== undefined) {
      return memoized;
    }

    if (visiting.has(scene.id)) {
      return false;
    }

    visiting.add(scene.id);
    let reachesEnding = false;

    for (const choice of scene.choices) {
      if (visit(choice.next_scene_id, visiting)) {
        reachesEnding = true;
      }
    }

    visiting.delete(scene.id);
    memo.set(scene.id, reachesEnding);
    return reachesEnding;
  }

  for (const sceneId of sceneById.keys()) {
    visit(sceneId, new Set());
  }

  return memo;
}

function hasReadyAsset(
  readyAssets: Map<string, Asset>,
  assetId: string,
  type: Asset["type"]
): boolean {
  return readyAssets.get(assetId)?.type === type;
}
